import React from 'react';
import {PageLayout} from '../../Layouts/PageLayout';
import DataGrid, {Column, FilterRow, HeaderFilter, Paging, Search} from 'devextreme-react/data-grid';
import {Button, Tooltip} from 'antd';
import {UserApp} from '../../Context/AuthProvider/AuthProviderActions';
import {useParameterizedQuery} from 'react-fetching-library';
import {blockUser, getUsersList} from '../../Actions/Users/UsersActions';
import modal from 'antd/lib/modal';
import { EditOutlined, ExclamationCircleOutlined} from '@ant-design/icons';
import {PageTitle} from '../../Components/PageTitle';
import {useLayoutState} from '../../Context/LayoutProvider/LayoutProvider';
import {useAuthState} from '../../Context/AuthProvider/AuthProvider';
import { LiaUserAltSlashSolid, LiaUserCheckSolid } from 'react-icons/lia';


import {UserInfo} from '../../Components/UserInfo';
import {useAlertDispatcherState} from '../../Context/AlertProvider/AlertProvider';
import {addAlert} from '../../Context/AlertProvider/AlertProviderActions';
import {UnblockUserModal} from './UnblockUserModal';
import {useNavigate, useParams} from 'react-router-dom';
import { ColumnCellTemplateData } from 'devextreme/ui/data_grid';

export const AdmonUsersPage: React.FunctionComponent = (_props) => {
    const [users, setUsers] = React.useState<UserApp[]>([]);
    const [unblockUser, setUnblockUser] = React.useState<UserApp>();

    const [keys, setKeys] = React.useState<number[]>([]);
    const [isLoading, setIsLoading] = React.useState<boolean>(false);

    const getUsersListAction = useParameterizedQuery(getUsersList);
    const blockUserAction = useParameterizedQuery(blockUser);

    const gridRef = React.useRef(null);

    const {isSmallScreen} = useLayoutState();

    const { user, token } = useAuthState();
    const alertDistpatcher = useAlertDispatcherState();

    const formatPhoneNumber = React.useCallback((phoneNumber: string) => {
        if (!phoneNumber) return '';
        if (phoneNumber.startsWith('+')) {
            return phoneNumber.replace(/(\+\d{1,3})(\d{2})(\d{4})(\d{4})/, '($1) $2-$3-$4');
        }
        return phoneNumber.replace(/(\d{3})(\d{3})(\d{4})/, '$1-$2-$3');
    }, []);

    const { userID } = useParams();
    const navigate = useNavigate();

    React.useEffect(() => {
        if (token === undefined) {
            return;
        }

        if(!isLoading){
            setIsLoading(true);
            setUsers([]);
            setKeys([]);

            getUsersListAction.query({}).then( res => {
                if (res.payload){
                    setUsers(res.payload.response.users);
                    setKeys(res.payload.response.keys);
                }
            } );
        }
    }, [getUsersListAction, isLoading, token]);

    const showModalConfirm = React.useCallback(() => {
        modal.confirm({
            title: 'Lo sentimos, no cuentas con llaves disponibles',
            content: 'Si deseas agregar un nuevo usuario, puedes desactivar a uno de los usuarios que actualmente tiene una llave asignada.\n' +
                'También puedes solicitar una nueva llave haciendo clic en "Sí, solicitar una llave nueva".',
            okText: 'Sí, solicitar una llave nueva',
            cancelText: 'Cancelar',
            prefixCls:'finish-migration-modal',
            icon: <ExclamationCircleOutlined style={{ color: 'var(--warning-color)' }} />,
        });
    }, []);

    const handleAddUser = React.useCallback( ()=>{
        if (keys.length === 0){
            showModalConfirm();
        }else{
            navigate('/add-user');
        }
    }, [keys.length, navigate, showModalConfirm]);

    const handlerBlockUser = React.useCallback( (user:UserApp)=>{
        blockUserAction.query(user).then( r => {
            if (r.payload !== undefined){
                setIsLoading(false);
                addAlert(r.payload.response, r.payload.response, 'success', alertDistpatcher);
            }
        } );
    }, [alertDistpatcher, blockUserAction] );

    const TITLE = React.useMemo(()=>{
        const buttons = [
            <Button  onClick={handleAddUser}
                     key="add-user"
                     type="primary"
                     block={isSmallScreen}
                     disabled={!isLoading || getUsersListAction.loading || user?.is_admin === false }
            >
                Agregar Usuario
            </Button>,
            <Button  onClick={ () => { setIsLoading(false); } }
                     key="refresh-users"
                     type="primary"
                     block={isSmallScreen}
                     disabled={!isLoading || getUsersListAction.loading}
                     loading={getUsersListAction.loading}
            >
                Actualizar
            </Button>,
        ];

        if(keys.length === 0){
            buttons[0] = (
                <Tooltip title="No hay llaves disponibles" placement="top" key="add-user-tooltip" >
                    { buttons[0] }
                </Tooltip>
            );
        }

        if( !user?.is_admin ){
            buttons.splice(0, 1);
        }

        return (
            <PageTitle title="Administracion de Usuarios" buttons={ buttons }/>
        );
    }, [handleAddUser, isSmallScreen, isLoading, getUsersListAction.loading, keys.length, user?.is_admin]);

    React.useEffect( ()=>{
        if (userID !== undefined && users.length > 0){
            const userIDInt = parseInt(userID);
            const user = users.find( user => user.id === userIDInt );
            if (user !== undefined){
                setUnblockUser(user);
                return;
            }else{
                addAlert('', 'El usuario no existe', 'error', alertDistpatcher);
            }
        }

        setUnblockUser(undefined);
    }, [userID, keys, users, alertDistpatcher] );

    return (
        <PageLayout title={TITLE}
                    disableOtherItems
                    isLoading={ getUsersListAction.loading }
                    loadingTip={ 'Obteniendo Lista de Usuarios...'}
        >
            <UnblockUserModal user={unblockUser} keys={ keys } isLoadingTable={setIsLoading}/>

                <DataGrid
                    ref={gridRef}
                    dataSource={users}
                    keyExpr="id"
                    showBorders={false}
                    className="datagrid-custom"
                    onEditorPreparing={(e) => {
                        if (e.dataField === 'telephone' && e.parentType === 'dataRow') {
                            if (e.value) {
                                e.editorOptions.value = e.value.replace(/\s+/g, '');
                            }
                        }
                    }}
                >
                    <HeaderFilter visible={true}>
                        <Search enabled={true}/>
                    </HeaderFilter>
                    <FilterRow visible={true}/>
                    <Paging enabled={true} defaultPageSize={10} />

                    <Column dataField="ids"
                            caption="Llave"
                            allowEditing={false}
                            dataType="string"
                            cssClass="non-editable-cell"
                            cellRender={(cellData: ColumnCellTemplateData) => {
                                const isBlocked = cellData.data.ids === undefined || cellData.data.ids === 0;
                                if (isBlocked){
                                    return '----';
                                }

                                return cellData.data.ids;
                            }}
                    />
                    <Column dataField="clave"
                            caption="Clave"
                            dataType="string"
                            cssClass="editable-cell"
                    />
                    <Column dataField="full_name"
                            caption="Nombre Completo"
                            dataType="string"
                            cssClass="editable-cell"
                            cellRender={ (cellData: ColumnCellTemplateData)=>{
                                const user:UserApp = cellData.data as UserApp;
                                return (
                                    <UserInfo user={ user } />
                                );
                            } }
                    />
                    <Column
                        dataField="email"
                        caption="Correo Electrónico"
                        allowEditing={true}
                        dataType="string"
                        cssClass={'editable-cell'}
                    />
                    <Column
                        dataField="telephone"
                        caption="Teléfono (Opcional)"
                        allowEditing={true}
                        headerCellRender={() => <span className="telephone-table-header">Teléfono (Opcional)</span>}
                        cellRender={(cellData) => {
                            const telephone = cellData.value;
                            const clearTelephone = telephone.replace(/\s/g, '').trim();

                            return formatPhoneNumber(clearTelephone);
                        }}
                        dataType="string"
                        cssClass="editable-cell"
                    />

                    <Column caption="Acciones"
                            width={120}
                            allowEditing={false}
                            cellRender={(cellData: ColumnCellTemplateData) => {
                                if (user === undefined) {
                                    return null;
                                }

                                const isAdmin = user.is_admin;
                                const isMy = user.id === cellData.data.id;
                                const isBlocked = cellData.data.ids === undefined || cellData.data.ids === 0;

                                const existKeys = keys.length > 0;
                                const userCell = cellData.data as UserApp;

                                return (
                                    <React.Fragment>
                                        { (isAdmin || isMy) && !isBlocked &&
                                        <Tooltip title="Editar">
                                            <Button
                                                type="link"
                                                icon={<EditOutlined />}
                                                size="small"
                                                onClick={() => {
                                                    navigate(`/edit-user/${ userCell.id }`);
                                                }}
                                                // disabled={isDisabled}
                                            />
                                        </Tooltip>
                                        }
                                        { isAdmin && !isMy && !isBlocked &&
                                        <Tooltip title="Bloquear">
                                            <Button
                                                type="link"
                                                icon={<LiaUserAltSlashSolid />}
                                                size="small"
                                                 onClick={() => handlerBlockUser(userCell)}
                                                disabled={ blockUserAction.loading }
                                                loading={ blockUserAction.loading }
                                            />
                                        </Tooltip>
                                        }

                                        { isAdmin && !isMy && isBlocked &&
                                        <Tooltip title={ existKeys ? 'Desbloquear' : 'No hay llaves disponibles'}>
                                            <Button
                                                type="link"
                                                icon={<LiaUserCheckSolid />}
                                                size="small"

                                                onClick={() => {
                                                    navigate(`/unblock/${ userCell.id }`);
                                                }}
                                                disabled={!existKeys}
                                            />
                                        </Tooltip>
                                        }
                                    </React.Fragment>
                                );
                            }}
                    />

                </DataGrid>

        </PageLayout>
    );
};
